import * as React from "react";
import * as ReactDOM from "react-dom";
import Chat from "./Chat";
import CloseChatModal from "./CloseChatModal";
import EmailModal from "./EmailModal";
import {
  Message,
  QuickReplyButton,
  DataField,
  SecureForm,
  Form
} from "../types";
import PrintableMessages from "./PrintableMessages";
import CobrowseConsentModal from "./CobrowseConsentModal";

interface ChatControllerProps {
  agentRating: number;
  agentName: string;
  agentHeadshotUrl: string;
  messages: Array<Message>;
  agentsTyping: Array<string>;
  visitorId: string;
  engagementId: string;
  engagementApiEndpoint: string;
  maxFileSizeBytes: number;
  fileSizeErrorMessage: string;
  siteId: number;
  isPopOut: boolean;
  quickReplies: QuickReplyButton[];
  dataField: DataField;
  form: Form;
  messageFormDisabled: boolean;
  cobrowseState: string;
  onClose: () => void;
  onPopout: () => void;
  onCollapse: () => void;
  onSendMessage: (text: string) => void;
  onClickQuickReply: (button: QuickReplyButton) => void;
  onSubmitDataField: (data: string) => void;
  onSubmitForm: (values: any) => void;
  onSetAgentRating: (rating: number) => void;
  onSubmitEmailAddress: (emailAddress: string) => void;
  onTyping: (message: string) => void;
  onSetLineRating: (lineId: number, rating: number) => void;
  onResendMessage(message: Message): void;
  onClickActionCard: (name: string, data: string) => void;
  secureForm: SecureForm;
}

interface ChatControllerState {
  showCloseChatModal: boolean;
  showEmailModal: boolean;
  uploadPercentage: number;
  showUploadProgress: boolean;
  renderPrintableMessages: boolean;
}

/**
 * This component's purpose is to make the Chat component as dumb as possible. The
 * Chat component needs to be consumed by agent console in the desinger preview.
 */

class ChatController extends React.Component<
  ChatControllerProps,
  ChatControllerState
> {
  state = {
    showCloseChatModal: false,
    showEmailModal: false,
    uploadPercentage: 0,
    showUploadProgress: false,
    renderPrintableMessages: false
  };

  renderCloseChatModal() {
    if (!this.state.showCloseChatModal) {
      return null;
    }

    return (
      <CloseChatModal
        onClickYes={this.props.onClose}
        onClickNo={() => this.setState({ showCloseChatModal: false })}
      />
    );
  }

  renderCobrowseConsentModal() {
    return <CobrowseConsentModal />;
  }

  onSubmitEmailAddres = (emailAddress: string) => {
    this.props.onSubmitEmailAddress(emailAddress);
    this.setState({ showEmailModal: false });
  };

  renderEmailModal() {
    if (!this.state.showEmailModal) {
      return null;
    }

    return (
      <EmailModal
        onSubmit={this.onSubmitEmailAddres}
        onCancel={() => this.setState({ showEmailModal: false })}
      />
    );
  }

  onUploadBegin = () => {
    this.setState({
      showUploadProgress: true
    });
  };

  onUploadComplete = () => {
    // the progress bar has a 600ms animation. we should let
    // that complete before hiding the component.

    setTimeout(() => {
      this.setState({
        uploadPercentage: 0,
        showUploadProgress: false
      });
    }, 600);
  };

  onUploadProgress = (percent: number) => {
    this.setState({ uploadPercentage: percent });
  };

  print() {
    // When the print button is clicked, we will render the printable messages
    // component into the dom. That div will be hidden by default, but displayed
    // with the `@media print` rule in css. The rest of the chat window will
    // be hidden by the `@media print` rule in css.
    //
    // We used to render the printable messages into a new window, call the
    // `print` method, then immediately close the window. The print dialog
    // in most browsers blocked the close method until the print dialog was
    // closed. Chrome on android does not block the window.close method for
    // obvious reasons so when the print window displayed, it was trying to
    // render a preview for a window that no longer existed.

    this.setState({ renderPrintableMessages: true }, () => {
      // We can't just do window.print because this code is executing
      // in a different iframe than the chat window is rendered to.
      const node = ReactDOM.findDOMNode(this);

      // document.print is really buggy in IE for some reason.
      // https://stackoverflow.com/a/29849410

      if (navigator.userAgent.match(/Trident/gi)) {
        node.ownerDocument.execCommand("print", false, null);
      } else {
        node.ownerDocument.defaultView.print();
      }
    });
  }

  render() {
    return (
      <React.Fragment>
        {this.state.renderPrintableMessages && (
          <div className="velaro-print-show">
            <PrintableMessages messages={this.props.messages} />
          </div>
        )}
        <div className="velaro-print-hide">
          {this.renderCloseChatModal()}
          {this.renderCobrowseConsentModal()}
          {this.renderEmailModal()}
          <Chat
            secureForm={this.props.secureForm}
            agentRating={this.props.agentRating}
            agentName={this.props.agentName}
            agentHeadshotUrl={this.props.agentHeadshotUrl}
            messages={this.props.messages}
            agentsTyping={this.props.agentsTyping}
            onPopout={this.props.onPopout}
            onCollapse={this.props.onCollapse}
            onSendMessage={this.props.onSendMessage}
            quickReplies={this.props.quickReplies}
            onClickQuickReply={this.props.onClickQuickReply}
            onSubmitDataField={this.props.onSubmitDataField}
            onSubmitForm={this.props.onSubmitForm}
            onSetAgentRating={this.props.onSetAgentRating}
            onTyping={this.props.onTyping}
            showUploadProgress={this.state.showUploadProgress}
            uploadPercentage={this.state.uploadPercentage}
            onShowCloseChatModal={() =>
              this.setState({ showCloseChatModal: true })
            }
            onClickEmailButton={() => this.setState({ showEmailModal: true })}
            onClickPrintButton={() => this.print()}
            onUploadBegin={this.onUploadBegin}
            onUploadProgress={this.onUploadProgress}
            onUploadComplete={this.onUploadComplete}
            visitorId={this.props.visitorId}
            engagementId={this.props.engagementId}
            engagementApiEndpoint={this.props.engagementApiEndpoint}
            maxFileSizeBytes={this.props.maxFileSizeBytes}
            fileSizeErrorMessage={this.props.fileSizeErrorMessage}
            siteId={this.props.siteId}
            onClickExit={() => this.setState({ showCloseChatModal: true })}
            onSetLineRating={this.props.onSetLineRating}
            isPopOut={this.props.isPopOut}
            onResendMessage={this.props.onResendMessage}
            dataField={this.props.dataField}
            form={this.props.form}
            onClickActionCard={this.props.onClickActionCard}
            messageFormDisabled={this.props.messageFormDisabled}
            cobrowseState={this.props.cobrowseState}
          />
        </div>
      </React.Fragment>
    );
  }
}

export default ChatController;
